import * as React from 'react';
import { useEffect, useRef, type ReactNode } from 'react';
import { HelpHint } from '@wit-support/ui';
import {
  PageToolbarSlot,
  useBreadcrumbDetailLabel,
  usePageDescriptionValue,
  type AdminClientModule,
  type ModuleRouteChrome,
} from '@wit-support/ui-domain';
import { breadcrumbsFor, headerTabSections } from '../lib/workspace-nav.js';
import { APP_TITLE, documentTitleFor } from '../lib/page-title.js';
import { HeaderSectionTabs, ShellBreadcrumbs } from './shell-nav.js';

/** id `<main>` оболочки — цель ссылки «Перейти к содержимому». */
export const SHELL_MAIN_ID = 'main-content';

/**
 * Ссылка «Перейти к содержимому». Оболочка ставит её ПЕРВЫМ ребёнком корня,
 * раньше `<aside>`: на десктопе сайдбар стоит в DOM до навбара, и иначе
 * первые Tab проходили бы всё меню, прежде чем дойти до неё. Видна только в
 * фокусе.
 */
export function SkipToContentLink(): React.ReactElement {
  return (
    <a
      href={`#${SHELL_MAIN_ID}`}
      // Переход фокусом, без `#main-content` в адресе (иначе роутер видит
      // чужой hash). `href` остаётся для случая без JS.
      onClick={(e) => {
        const main = document.getElementById(SHELL_MAIN_ID);
        if (!main) return;
        e.preventDefault();
        main.focus();
      }}
      className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-[var(--radius-2)] focus:bg-[var(--color-surface-raised)] focus:px-3 focus:py-2 focus:text-sm focus:shadow-md"
    >
      Перейти к содержимому
    </a>
  );
}

export interface ShellHeaderProps {
  /** Хром совпавшего маршрута (`shellChromeOf`). */
  chrome: ModuleRouteChrome;
  /** Полный реестр модулей — для крошек скрытых из навигации модулей. */
  modules: readonly AdminClientModule[];
  /** Доступные оператору модули. */
  navModules: readonly AdminClientModule[];
  path: string;
  /** Модуль текущего маршрута (для вкладок подразделов). */
  currentModule: AdminClientModule | undefined;
  /** Гамбургер с мобильной навигацией (виден ниже `lg`). */
  navMenu: ReactNode;
  /** Меню пользователя ниже `lg` (на десктопе оно в сайдбаре). */
  userMenu: ReactNode;
  /** Фактическая высота навбара, px — оболочка кладёт её в `--shell-header-h`. */
  onHeightChange?: (px: number) => void;
}

/**
 * Навбар оболочки.
 *
 * `chrome: 'page'` (все экраны, не объявившие иное) — разметка прежняя:
 * гамбургер, крошки, вкладки подразделов, меню пользователя; навбар не липкий.
 *
 * `chrome: 'shell'` — липкий (`sticky top-0 z-30`, ниже оверлеев `z-50`):
 * - первая строка ровно `h-14`: гамбургер, крошки (сжимаются первыми, ниже
 *   `xl`/`lg` теряют ведущие звенья), ⓘ-описание экрана, вкладки, слот
 *   тулбара страницы справа, меню пользователя;
 * - ниже `md` слот уходит второй строкой во всю ширину с горизонтальной
 *   прокруткой; пустой слот скрыт (`:empty`) и строки не добавляет.
 *
 * Ссылки «Перейти к содержимому» здесь нет: она первая во всей оболочке
 * (`SkipToContentLink` в `app-shell.tsx`), раньше сайдбара.
 */
export function ShellHeader({
  chrome,
  modules,
  navModules,
  path,
  currentModule,
  navMenu,
  userMenu,
  onHeightChange,
}: ShellHeaderProps): React.ReactElement {
  const ref = useRef<HTMLElement | null>(null);
  const description = usePageDescriptionValue();
  const detail = useBreadcrumbDetailLabel();
  const crumbs = breadcrumbsFor({ modules, navModules, path, detail });
  const current = crumbs[crumbs.length - 1]?.label ?? '';
  // Подразделы активного модуля в режиме 'tabs' живут в навбаре; рядом с ними
  // крошки уступают место и на узком экране прячутся совсем.
  const hasHeaderTabs = headerTabSections(currentModule).length > 0;

  useEffect(() => {
    const el = ref.current;
    if (!el || !onHeightChange || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => onHeightChange(el.getBoundingClientRect().height));
    ro.observe(el);
    return () => ro.disconnect();
  }, [onHeightChange]);

  if (chrome !== 'shell') {
    return (
      <header
        ref={ref}
        className="flex h-14 items-center gap-2 border-b border-[var(--color-border-default)] px-3 sm:px-4 lg:px-6"
      >
        {navMenu}
        <ShellBreadcrumbs
          modules={modules}
          navModules={navModules}
          path={path}
          className={[
            'truncate text-sm text-[var(--color-text-muted)]',
            // Рядом с вкладками подразделов хлебные крошки уступают им место и
            // на узком экране прячутся совсем: раздел там называет активная
            // вкладка. Без вкладок класс тот же, что и до их появления.
            hasHeaderTabs ? 'hidden min-w-0 shrink sm:block' : 'flex-1',
          ].join(' ')}
        />
        <HeaderSectionTabs module={currentModule} path={path} />
        <div className="lg:hidden">{userMenu}</div>
      </header>
    );
  }

  return (
    <header
      ref={ref}
      data-chrome="shell"
      className="sticky top-0 z-30 flex min-h-14 flex-wrap items-center gap-x-2 border-b border-[var(--color-border-default)] bg-[var(--color-surface-canvas)] px-3 sm:px-4 lg:px-6"
    >
      {navMenu}
      <div
        className={[
          'h-14 min-w-0 shrink items-center gap-1',
          hasHeaderTabs ? 'hidden sm:flex' : 'flex',
        ].join(' ')}
      >
        <ShellBreadcrumbs
          modules={modules}
          navModules={navModules}
          path={path}
          collapseLeading
          className="min-w-0 truncate text-sm text-[var(--color-text-muted)]"
        />
        {description ? <HelpHint label={`Об экране «${current}»`}>{description}</HelpHint> : null}
      </div>
      <HeaderSectionTabs module={currentModule} path={path} />
      <PageToolbarSlot className="max-md:order-last max-md:basis-full max-md:justify-start max-md:overflow-x-auto max-md:pb-2 md:min-w-[var(--page-toolbar-min)] md:flex-1" />
      <div className="ml-auto lg:hidden">{userMenu}</div>
    </header>
  );
}

export interface ShellPageTitleProps {
  chrome: ModuleRouteChrome;
  modules: readonly AdminClientModule[];
  navModules: readonly AdminClientModule[];
  path: string;
}

/**
 * Заголовок экрана, которого экран в режиме `'shell'` сам не рисует.
 *
 * - `document.title` из крошек — для ВСЕХ маршрутов («Сводка · Окупаемость ·
 *   WIT Admin»). Деталь крошки из `useBreadcrumbDetail` в него НЕ входит:
 *   там бывают персональные данные (автор комментария в соцсети), а title
 *   уходит в историю браузера. Вместо детали — подпись маршрута («Клип»).
 *   Модули `document.title` не задают (на 2026-10-22 ни один); если всё же
 *   задаст, оболочка его не перетрёт: в пределах экрана она пишет title,
 *   только пока он равен её же прошлой записи. Переход на другой адрес
 *   возвращает title оболочке — иначе чужой заголовок прошлого экрана
 *   остался бы во вкладке навсегда.
 * - Скрытый `<h1>` = последняя крошка (с деталью) — только в режиме
 *   `'shell'`: экраны `'page'` рисуют свой `PageHeader`, и второй `<h1>` был
 *   бы лишним.
 *
 * Стоит внутри `<main>` под `BreadcrumbDetailProvider`.
 */
export function ShellPageTitle({
  chrome,
  modules,
  navModules,
  path,
}: ShellPageTitleProps): React.ReactElement | null {
  const detail = useBreadcrumbDetailLabel();
  const crumbs = breadcrumbsFor({ modules, navModules, path, detail });
  const title = documentTitleFor(breadcrumbsFor({ modules, navModules, path, detail: null }));
  // Что оболочка записала в прошлый раз и для какого адреса.
  const written = useRef<{ title: string; path: string } | null>(null);
  useEffect(() => {
    const prev = written.current;
    // Свой экран, а title уже не наш — его поставил модуль: не трогаем.
    if (prev !== null && prev.path === path && document.title !== prev.title) return;
    document.title = title;
    written.current = { title, path };
  }, [title, path]);
  // Выход из админки (размонтирование оболочки) — вкладке возвращается общий title.
  useEffect(
    () => () => {
      document.title = APP_TITLE;
    },
    [],
  );
  if (chrome !== 'shell') return null;
  const current = crumbs[crumbs.length - 1]?.label ?? '';
  return current ? <h1 className="sr-only">{current}</h1> : null;
}
